import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from '@/core/http/client';
import { AuthProvider } from '@/core/auth/AuthProvider';
import { ConfigProvider } from '@/core/config/ConfigProvider';
import { Alert, Button, Container, Skeleton } from '@/design-system';

/**
 * Cliente de query unico. As politicas ficam aqui e nao espalhadas por
 * chamada: erro de negocio (4xx) nao adianta repetir, falha transitoria sim.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 15 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) return error.isRetryable && failureCount < 2;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Mutacao nunca repete sozinha: cobranca duplicada e pior que erro.
      retry: false,
    },
  },
});

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  public state: { error: Error | null } = { error: null };

  public static getDerivedStateFromError(error: Error) {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // Ponto de envio para o coletor de erros (Sentry/OTel) em producao.
    console.error('[app]', error, info.componentStack);
  }

  public render() {
    if (!this.state.error) return this.props.children;
    return (
      <Container>
        <div className="ds-stack" style={{ paddingBlock: 'var(--space-8)' }}>
          <h1>Nao foi possivel carregar o portal</h1>
          <Alert tone="danger">{this.state.error.message}</Alert>
          <div>
            <Button onClick={() => window.location.reload()}>Recarregar</Button>
          </div>
        </div>
      </Container>
    );
  }
}

function BootSkeleton() {
  return (
    <Container>
      <div className="flex flex-col gap-4 py-16">
        <Skeleton className="h-12 w-2/5" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </Container>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Suspense fallback={<BootSkeleton />}>
            <ConfigProvider>{children}</ConfigProvider>
          </Suspense>
        </AuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
